import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';

import {DescriptionList} from 'components/DescriptionList';
import CheckboxOption from 'components/CheckboxOption';
import FormStack from 'components/FormStack';
import Button from 'components/Button';
import ScormAdvancedSettingsModal from 'screens/ScormAdvancedSettingsModal';

import FileNameDescription from '../Descriptions/FileNameDescription';
import FileSizeDescription from '../Descriptions/FileSizeDescription';
import LastUpdatedDescription from '../Descriptions/LastUpdatedDescription';
import AssetLibraryDescription from '../Descriptions/AssetLibraryDescription';

import DividerPanel from './DividerPanel';
import ProcessingAlert from './ProcessingAlert';

import {ASSET_WRITE, hasPrivilege} from 'utils/privileges';

const ScormDetailsPanel = ({
	isEditMode,
	isNewFile,
	isProcessingError,
	uploadProgress,
	showUploadProgress,
	content,
	fields,
	onReplace,
	onDelete,
}) => {
	const hasAssetPermissions = hasPrivilege(ASSET_WRITE);
	const {id, size, fileName} = content;

	const {lastUpdated, scormSettings, openInIframe, parentId} = fields;

	const {t} = useTranslation();
	const [showAdvancedSettingsModal, setShowAdvancedSettingsModal] =
		useState(false);

	return (
		<DividerPanel header={t('content_type_scorm_details')}>
			<FormStack spacing="m">
				{isProcessingError && (
					<ProcessingAlert
						isEditMode={isEditMode}
						fileName={fileName}
						onReplace={onReplace}
						onDelete={onDelete}
					/>
				)}
				<DescriptionList titleWidth={1 / 4}>
					<FileNameDescription
						isEditMode={isEditMode}
						isNewFile={isNewFile}
						assetId={id}
						fileName={fileName}
						onReplace={onReplace}
					/>
					<FileSizeDescription
						showUploadProgress={showUploadProgress}
						size={size}
						uploadProgress={uploadProgress}
					/>
					<LastUpdatedDescription lastUpdated={lastUpdated?.value} />
					{hasAssetPermissions && (
						<AssetLibraryDescription
							content={content}
							updateFolderLocation={value => parentId.set(value)}
							folderId={parentId.value}
						/>
					)}
				</DescriptionList>

				<Button
					size="medium"
					variant="transparent"
					onClick={() => setShowAdvancedSettingsModal(true)}
					ml="-xs"
				>
					{t('scorm_settings_title')}
				</Button>

				<CheckboxOption
					fontSize="s"
					label={t('actions_open_in_new_window')}
					id="open-in-new-window"
					checked={!openInIframe.value}
					onChange={e => openInIframe.set(!e.target.checked)}
				/>
			</FormStack>
			{showAdvancedSettingsModal && (
				<ScormAdvancedSettingsModal
					onChange={newSettings => scormSettings.set(newSettings)}
					defaultSettings={scormSettings.value}
					onClose={() => setShowAdvancedSettingsModal(false)}
					onSave={() => setShowAdvancedSettingsModal(false)}
					isButtonDisabled={!scormSettings.wasChanged}
				/>
			)}
		</DividerPanel>
	);
};

export default ScormDetailsPanel;

ScormDetailsPanel.propTypes = {
	isEditMode: PropTypes.bool,
	isNewFile: PropTypes.bool,
	isProcessingError: PropTypes.bool,
	showUploadProgress: PropTypes.bool,
	uploadProgress: PropTypes.number,
	content: PropTypes.object,
	fields: PropTypes.object,
	onReplace: PropTypes.func,
	onDelete: PropTypes.func,
};
